import { useEffect, useState } from 'react';
import { api } from '../api/client';

/**
 * The team members a task can be assigned to, and whether the current user may
 * manage the whole company's tasks.
 *
 * Lives in a hook because three places need the same answer (the timeline's
 * assignee picker, the Team Tasks page, and the nav item that links to it) and
 * the client cannot work it out for itself: AuthContext only carries the legacy
 * `role` string, while an employee invited through invite-employee has their
 * real role on CompanyEmployee.role_id. The backend decides — see
 * canManageTeamTasks in src/routes/activities.js.
 *
 * The response is cached at module scope so mounting several timelines on one
 * page (or navigating between records) does not refetch it. `resetTaskTeam`
 * exists for tests and for a future company switch.
 */
export interface TeamMember { id: string; name: string; email: string; }

interface TaskTeam {
  users: TeamMember[];
  canManageTeam: boolean;
  me: string | null;
  loaded: boolean;
}

const EMPTY: TaskTeam = { users: [], canManageTeam: false, me: null, loaded: false };

let cache: TaskTeam | null = null;
let inflight: Promise<TaskTeam> | null = null;

export const resetTaskTeam = () => { cache = null; inflight = null; };

async function fetchTaskTeam(): Promise<TaskTeam> {
  if (cache) return cache;
  if (!inflight) {
    inflight = api
      .get<{ users: TeamMember[]; can_manage_team: boolean; me: string }>('/activities/assignable-users')
      .then((res) => {
        cache = {
          users: res.data.users || [],
          canManageTeam: !!res.data.can_manage_team,
          me: res.data.me || null,
          loaded: true,
        };
        return cache;
      })
      .catch(() => {
        // Never let this break the page it is decorating — an unreachable
        // endpoint just means no picker and no team view, not a crash.
        inflight = null;
        return { ...EMPTY, loaded: true };
      });
  }
  return inflight;
}

export function useTaskTeam(): TaskTeam {
  const [team, setTeam] = useState<TaskTeam>(cache || EMPTY);

  useEffect(() => {
    let alive = true;
    fetchTaskTeam().then((t) => { if (alive) setTeam(t); });
    return () => { alive = false; };
  }, []);

  return team;
}
